<template>
  <div id="app" class="d-flex flex-column min-vh-100">
    <nav id="nav" v-if="currentUser" ref="nav"
         class="navbar bg-faded navbar-dark bg-dark justify-content-center"
         :class="navFits ? 'navbar-expand' : 'nav--stacked'">
      <div class="container-fluid nav-shell" ref="navShell">
        <a v-if="useFunctionality !== 'WEBSITE'" href="/" class="navbar-brand d-flex me-auto"><img src="@/assets/images/logo.png" width="60" alt="CAGISTA" /></a>
        <a v-else href="#" class="navbar-brand d-flex me-auto"><img src="@/assets/images/logo.png" width="60" alt="CAGISTA" /></a>

        <button class="navbar-toggler border-0 shadow-none" type="button" data-bs-toggle="collapse"
                data-bs-target="#collapsingNavbar3" aria-controls="collapsingNavbar3" aria-expanded="false" aria-label="Toggle navigation">
          <span class="hamburger-icon">
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>

        <div class="navbar-collapse collapse w-100 mobile-nav" id="collapsingNavbar3">
          <ul class="navbar-nav bg-faded navbar-dark bg-dark ms-auto" ref="navList">
            <!-- SUPER_ADMIN sees everything the merchant has switched on, and not what the merchant has not (founder,
                 2026-11-01: «superadmin skal kunne se alt som merchant har slått på»). So a gate on the merchant's
                 features holds for SUPER_ADMIN too. The «|| isSuperAdmin» below are older decisions the founder kept,
                 and SUPER_ADMIN's own tools. «Show SUPER_ADMIN every entry» was tried that day and taken back. -->
            <li v-if="useFunctionality !== 'WEBSITE'" class="nav-item">
              <a class="nav-link" href="/overview">Oversikt</a>
            </li>
            <li v-if="useFunctionality !== 'WEBSITE' && !isAccountant" class="nav-item">
              <a class="nav-link" href="/orders">Ordrer</a>
            </li>
            <!-- Staff check whether a payment went through too. They have no Rapporter menu, so the page is a top-level
                 entry for them; ADMIN finds it under Rapporter. -->
            <li v-if="isEmployee && usesViva" class="nav-item">
              <a class="nav-link" href="/vivaPaymentTransactions">Kortbetalinger</a>
            </li>
            <li v-if="tableBookingEnabled && !isAccountant" class="nav-item">
              <a class="nav-link" href="/bookings">Bordbookinger</a>
            </li>
            <li v-if="useFunctionality !== 'WEBSITE' && useOnlinePayment && !isEmployee" class="nav-item dropdown">
              <a class="nav-link dropdown-toggle" href="#" id="reportScrollingDropdown" role="button"
                 data-bs-toggle="dropdown" aria-expanded="false">Rapporter</a>
              <ul class="dropdown-menu dropdown-menu-right" aria-labelledby="reportScrollingDropdown">
                <li v-if="!isAccountant" class="container"><a class="dropdown-item" href="/x-report">X-Rapport</a></li>
                <li class="container"><a class="dropdown-item" href="/z-report">Z-Rapport (Dagsoppgjør)</a></li>
                <li v-if="useFunctionality !== 'WEBSITE' && useFunctionality !== 'TAKEAWAY' && useOnlinePayment" class="container"><a class="dropdown-item" href="/cashHoldingReport">Kontantbeholdning</a></li>
                <li class="container"><a class="dropdown-item" href="/totalSalesReport">Omsetningsrapport</a></li>
                <!-- Alcohol is served where guests eat in, so only a merchant with a dine-in location gets the report
                     (founder, 2026-11-01): see hasDineIn. A takeaway like mollapizza, whose till and kiosk offer only
                     «Ta med», does not. -->
                <li v-if="useFunctionality !== 'WEBSITE' && useFunctionality !== 'TAKEAWAY' && useOnlinePayment && hasDineIn" class="container"><a class="dropdown-item" href="/alcoholReport">Alkoholrapport</a></li>
                <!-- For a merchant who pays for SMS, and for SUPER_ADMIN (founder, 2026-11-01). -->
                <li v-if="(payForSMS || isSuperAdmin) && !isAccountant" class="container"><a class="dropdown-item" href="/smsReport">SMS Rapport</a></li>
                <li v-if="useFunctionality !== 'WEBSITE' && useFunctionality !== 'TAKEAWAY' && useOnlinePayment" class="container"><a class="dropdown-item" href="/saftReport">SAF-T Rapport</a></li>
                <!-- Kitchen times are written by the KDS, so without one the report could only be empty. SUPER_ADMIN
                     sees it regardless, to know the report is there. -->
                <li v-if="(usesKds || isSuperAdmin) && !isAccountant" class="container"><a class="dropdown-item" href="/kitchenTimesReport">Kjøkkentid</a></li>
                <!-- Did a payment go through: the merchant's own VIVA transactions, for a merchant that takes VIVA on the
                     web or at a till. Not offered to a merchant without VIVA, legacy merchants among them. EMPLOYEE has
                     no Rapporter menu and gets a top-level entry instead. -->
                <li v-if="(usesViva || isSuperAdmin) && !isAccountant" class="container"><a class="dropdown-item" href="/vivaPaymentTransactions">Kortbetalinger</a></li>
                <!-- What VIVA paid out to the bank account: the owner's figures, so ADMIN only (EMPLOYEE has no Rapporter
                     menu, and ACCOUNTANT is kept to its read-only list). -->
                <li v-if="(usesViva || isSuperAdmin) && !isAccountant" class="container"><a class="dropdown-item" href="/vivaPayoutReports">Utbetalinger</a></li>
                <!-- The accountant's page: VIVA's card settlement per day with its fees, and the way to the reports that
                     document the sales. ADMIN, ACCOUNTANT and SUPER_ADMIN since 2026-11-01 (SUPER_ADMIN only while it was
                     tested in production); EMPLOYEE has no Rapporter menu, and the fees are the owner's figures. Shown
                     without VIVA too, where it says so and still links the other bilag. Under Utbetalinger: it is
                     what each payout consists of (founder, 2026-11-01). -->
                <li class="container"><a class="dropdown-item" href="/accountingVouchers">Regnskapsbilag</a></li>
                <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/saftUploadReport">SAF-T: Extract omsetningsrapport</a></li>
                <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/generateEventReports">SAF-T: Generate eventReport for Z reports</a></li>
                <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/transactionsReport">Transaksjoner</a></li>
                <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/xReportRegenerate">(Superadmin) Regenerere oppgjørsrapport for en bestemt dag</a></li>
              </ul>
            </li>
            <li v-if="!isAccountant" class="nav-item">
              <a class="nav-link" href="/products">Produkter</a>
            </li>
            <li v-if="useFunctionality !== 'WEBSITE' && (offerDiscount || offerRewardProgram) && !isEmployee && !isAccountant" class="nav-item dropdown">
              <a class="nav-link dropdown-toggle" href="#" id="loyaltyDropdown" role="button"
                 data-bs-toggle="dropdown" aria-expanded="false">Lojalitet</a>
              <ul class="dropdown-menu" aria-labelledby="loyaltyDropdown">
                <li v-if="offerDiscount" class="container"><a class="dropdown-item" href="/discounts">Rabatter</a></li>
                <li v-if="offerRewardProgram" class="container"><a class="dropdown-item" href="/rewards">Bonusprogram</a></li>
              </ul>
            </li>
            <li v-if="showUsersPage && !isEmployee && !isAccountant" class="nav-item">
              <a class="nav-link" href="/users">Brukere</a>
            </li>
            <li v-if="useFunctionality !== 'WEBSITE' && !isEmployee" class="nav-item">
              <a class="nav-link" :class="{ 'nav-link--disabled': analyticsLoading, active: isActive('/analytics') }" href="/analytics" @click.prevent="handleAnalyticsClick">Innsikt</a>
            </li>
            <li v-if="isEmployee" class="nav-item dropdown">
              <a class="nav-link dropdown-toggle" href="#" id="settingsDropdown" role="button"
                 data-bs-toggle="dropdown" aria-expanded="false">Innstillinger</a>
              <ul class="dropdown-menu" aria-labelledby="settingsDropdown">
                <li class="container"><a class="dropdown-item" href="/announcements">Meldinger</a></li>
                <li class="container"><a class="dropdown-item" href="/closingdays">Stengte dager</a></li>
              </ul>
            </li>
            <li v-if="!isEmployee && !isAccountant" class="nav-item dropdown">
              <a class="nav-link dropdown-toggle" href="#" id="settingsDropdown" role="button"
                 data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">Innstillinger</a>
              <ul class="dropdown-menu" aria-labelledby="settingsDropdown">
                <li class="container"><a class="dropdown-item" href="/announcements">Meldinger</a></li>
                <li class="container"><a class="dropdown-item" href="/closingdays">Stengte dager</a></li>
                <li class="container"><a class="dropdown-item" href="/openinghours">Åpningstider</a></li>
                <li v-if="isSuperAdmin || useFunctionality === 'KIOSK_TAKEAWAY' || useFunctionality === 'ALL'" class="container"><a class="dropdown-item" href="/tables">Bord</a></li>
                <li v-if="useFunctionality !== 'WEBSITE'" class="container"><a class="dropdown-item" href="/bookingSettings">Bordbooking</a></li>
                <li v-if="offerDeliveryService && useFunctionality !== 'WEBSITE'" class="container"><a class="dropdown-item" href="/delivery">Levering</a></li>
                <li v-if="useFunctionality !== 'WEBSITE'" class="container"><a class="dropdown-item" href="/vat">Merverdiavgift (MVA)</a></li>
                <li v-if="useFunctionality === 'POS' || useFunctionality === 'KIOSK_TAKEAWAY' || useFunctionality === 'ALL'" class="container"><a class="dropdown-item" href="/terminals">Terminaler</a></li>
                <li class="container"><a class="dropdown-item" href="/company">Firma</a></li>
                <li v-if="useSaaSFeatures || isSuperAdmin" class="container"><a class="dropdown-item" href="/siteBuilder">Website Builder</a></li>
                <li v-if="useSaaSFeatures || isSuperAdmin" class="container"><a class="dropdown-item" href="/customDomain">Eget domene</a></li>
                <li v-if="useSaaSFeatures || isSuperAdmin" class="container"><a class="dropdown-item" href="/vivaOnboarding">Aktiver VIVA-betaling</a></li>
                <li v-if="useSaaSFeatures || isSuperAdmin" class="container"><a class="dropdown-item" href="/odsDisplays">Kundeklarskjerm (ODS)</a></li>
                <li v-if="useSaaSFeatures || isSuperAdmin" class="container"><a class="dropdown-item" href="/billing">Abonnement og betaling</a></li>
                <li v-if="isSuperAdmin || hasMultiLocations" class="container"><a class="dropdown-item" href="/locations">Avdelinger</a></li>
                <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/merchant">Merchant</a></li>
                <li v-if="isSuperAdmin" class="container">
                  <a class="dropdown-item has-submenu" href="#" @click.prevent.stop="showPspSubmenu = !showPspSubmenu">
                    PSP
                    <font-awesome-icon :icon="['fas', showPspSubmenu ? 'chevron-up' : 'chevron-down']" class="submenu-arrow" />
                  </a>
                  <ul v-show="showPspSubmenu" class="list-unstyled ps-3">
                    <li class="container"><a class="dropdown-item" href="/swedbankPay">SwedbankPay eCom</a></li>
                    <li class="container"><a class="dropdown-item" href="/dintero">Dintero</a></li>
                  </ul>
                </li>
                <!-- Only the accounting systems this merchant actually uses. The platform supports a
                     growing list and will support many more; showing all of them would make the menu a
                     catalogue of things that do not apply to the merchant looking at it. Hidden
                     entirely when no location has one — a location's accountingSystem is set under
                     Avdelinger, which is the step that comes first anyway. -->
                <li v-if="isSuperAdmin && usesAnyAccountingSystem" class="container">
                  <a class="dropdown-item has-submenu" href="#" @click.prevent.stop="showRegnskapSubmenu = !showRegnskapSubmenu">
                    Regnskap
                    <font-awesome-icon :icon="['fas', showRegnskapSubmenu ? 'chevron-up' : 'chevron-down']" class="submenu-arrow" />
                  </a>
                  <ul v-show="showRegnskapSubmenu" class="list-unstyled ps-3">
                    <li v-if="usesUnimicro" class="container">
                      <a class="dropdown-item has-submenu" href="#" @click.prevent.stop="showUnimicroSubmenu = !showUnimicroSubmenu">
                        Unimicro
                        <font-awesome-icon :icon="['fas', showUnimicroSubmenu ? 'chevron-up' : 'chevron-down']" class="submenu-arrow" />
                      </a>
                      <ul v-show="showUnimicroSubmenu" class="list-unstyled ps-3">
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/unimicroSyncZReport">Synkroniser Z-rapport (Dagsoppgjør)</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/unimicroDeleteVouchers">Krediter dagsoppgjør-bilag</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/unimicroSyncOrders">Synkroniser fakturaordrer</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/invoiceCustomers">Fakturakunder</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/unimicroDepartments">Avdelinger</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/unimicroAccountSettings">Kontoinnstillinger</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/unimicro">Aktivering</a></li>
                      </ul>
                    </li>
                    <li v-if="usesFiken" class="container">
                      <a class="dropdown-item has-submenu" href="#" @click.prevent.stop="showFikenSubmenu = !showFikenSubmenu">
                        Fiken
                        <font-awesome-icon :icon="['fas', showFikenSubmenu ? 'chevron-up' : 'chevron-down']" class="submenu-arrow" />
                      </a>
                      <ul v-show="showFikenSubmenu" class="list-unstyled ps-3">
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/fikenSyncZReport">Synkroniser Z-rapport (Dagsoppgjør)</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/fikenDeleteVouchers">Slett dagsoppgjør-bilag</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/fikenSyncOrders">Synkroniser fakturaordrer</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/invoiceCustomers">Fakturakunder</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/fikenCompanies">Firmaer</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/fikenAccountSettings">Kontoinnstillinger</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/fiken">Fiken konto</a></li>
                      </ul>
                    </li>
                    <li v-if="usesTripletex" class="container">
                      <a class="dropdown-item has-submenu" href="#" @click.prevent.stop="showTripletexSubmenu = !showTripletexSubmenu">
                        Tripletex
                        <font-awesome-icon :icon="['fas', showTripletexSubmenu ? 'chevron-up' : 'chevron-down']" class="submenu-arrow" />
                      </a>
                      <ul v-show="showTripletexSubmenu" class="list-unstyled ps-3">
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/tripletexSyncZReport">Synkroniser Z-rapport (Dagsoppgjør)</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/tripletexDeleteVouchers">Slett dagsoppgjør-bilag</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/tripletexSyncSettlements">Synkroniser betalingsoppgjør</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/tripletexSyncOrders">Synkroniser fakturaordrer</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/tripletexSyncProducts">Synkroniser produkter</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/invoiceCustomers">Fakturakunder</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/tripletexDepartments">Avdelinger</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/tripletexAccountSettings">Kontoinnstillinger</a></li>
                        <li v-if="isSuperAdmin" class="container"><a class="dropdown-item" href="/tripletex">Tripletex konto</a></li>
                      </ul>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
            <li class="nav-item dropdown">
              <a class="nav-link dropdown-toggle" href="#" id="supportDropdown" role="button"
                 data-bs-toggle="dropdown" aria-expanded="false">Support</a>
              <ul class="dropdown-menu" aria-labelledby="supportDropdown">
                <li v-if="useSaaSFeatures" class="container"><a class="dropdown-item" href="#" @click.prevent="startTraining">Opplæring</a></li>
                <li v-if="!isAccountant" class="container"><a class="dropdown-item" href="/posuserguide">Brukerveiledning (Kasse)</a></li>
                <li v-if="!isAccountant" class="container"><a class="dropdown-item" href="/backofficeuserguide">Brukerveiledning (Backoffice)</a></li>
                <li v-if="!isAccountant" class="container"><a class="dropdown-item" href="/helpvideo">Hjelpevideo</a></li>
                <li class="container"><a class="dropdown-item" href="/bokettersyn">Bokettersyn og kontrollbesøk</a></li>
                <li class="container"><a class="dropdown-item" href="/terms">Vilkår</a></li>
                <li class="container"><a class="dropdown-item" href="/privacy">Personvern</a></li>
                <li class="container"><a class="dropdown-item" href="/dpa">Databehandleravtale</a></li>
                <!-- In Support, where a merchant already goes when something is wrong — not a floating
                     bubble in the corner, which is a pattern this app uses nowhere else. Last in the
                     list: the entries above are things to READ, this is the one that writes back. -->
                <li class="container"><a class="dropdown-item" href="#" @click.prevent="openFeedback">Gi tilbakemelding</a></li>
              </ul>
            </li>
            <li v-if="isSuperAdmin" class="nav-item dropdown">
              <a class="nav-link dropdown-toggle" href="#" id="systemDropdown" role="button"
                 data-bs-toggle="dropdown" aria-expanded="false">System</a>
              <ul class="dropdown-menu" aria-labelledby="systemDropdown">
                <li class="container"><a class="dropdown-item" href="/sendIncidentSMS">Send hendelsesvarsel (SMS)</a></li>
                <li class="container">
                  <a class="dropdown-item has-submenu" href="#" @click.prevent.stop="showPspTransactionsSubmenu = !showPspTransactionsSubmenu">
                    PSP Transactions
                    <font-awesome-icon :icon="['fas', showPspTransactionsSubmenu ? 'chevron-up' : 'chevron-down']" class="submenu-arrow" />
                  </a>
                  <ul v-show="showPspTransactionsSubmenu" class="list-unstyled ps-3">
                    <li class="container"><a class="dropdown-item" href="/swedbankPayTransactions">SwedbankPay Transactions</a></li>
                    <li class="container"><a class="dropdown-item" href="/dinteroTransactions">Dintero Transactions</a></li>
                    <li class="container"><a class="dropdown-item" href="/vivaTransactions">Viva Transactions</a></li>
                  </ul>
                </li>
                <li class="container"><a class="dropdown-item" href="/mdm">CAGISTA MDM</a></li>
                <li class="container"><a class="dropdown-item" href="/merchants">Merchants</a></li>
                <li class="container"><a class="dropdown-item" href="/syncGatewayUsers">Sync Gateway users</a></li>
                <li class="container">
                  <a class="dropdown-item has-submenu" href="#" @click.prevent.stop="showConfigSubmenu = !showConfigSubmenu">
                    Config
                    <font-awesome-icon :icon="['fas', showConfigSubmenu ? 'chevron-up' : 'chevron-down']" class="submenu-arrow" />
                  </a>
                  <ul v-show="showConfigSubmenu" class="list-unstyled ps-3">
                    <li class="container">
                      <a class="dropdown-item has-submenu" href="#" @click.prevent.stop="showConfigPlatformSubmenu = !showConfigPlatformSubmenu">
                        Plattform
                        <font-awesome-icon :icon="['fas', showConfigPlatformSubmenu ? 'chevron-up' : 'chevron-down']" class="submenu-arrow" />
                      </a>
                      <ul v-show="showConfigPlatformSubmenu" class="list-unstyled ps-3">
                        <li class="container"><a class="dropdown-item" href="/s3Config">S3 Config</a></li>
                        <li class="container"><a class="dropdown-item" href="/smtpConfig">SMTP Config</a></li>
                      </ul>
                    </li>
                    <li class="container">
                      <a class="dropdown-item has-submenu" href="#" @click.prevent.stop="showConfigDevicesSubmenu = !showConfigDevicesSubmenu">
                        Enheter
                        <font-awesome-icon :icon="['fas', showConfigDevicesSubmenu ? 'chevron-up' : 'chevron-down']" class="submenu-arrow" />
                      </a>
                      <ul v-show="showConfigDevicesSubmenu" class="list-unstyled ps-3">
                        <li class="container"><a class="dropdown-item" href="/amapiConfig">AMAPI Config</a></li>
                        <li class="container"><a class="dropdown-item" href="/rustdeskConfig">RustDesk Config</a></li>
                      </ul>
                    </li>
                    <li class="container">
                      <a class="dropdown-item has-submenu" href="#" @click.prevent.stop="showConfigStoreSubmenu = !showConfigStoreSubmenu">
                        Nettbutikk
                        <font-awesome-icon :icon="['fas', showConfigStoreSubmenu ? 'chevron-up' : 'chevron-down']" class="submenu-arrow" />
                      </a>
                      <ul v-show="showConfigStoreSubmenu" class="list-unstyled ps-3">
                        <li class="container"><a class="dropdown-item" href="/storeVivaAccount">Kassasystembutikk VIVA-konto</a></li>
                        <li class="container"><a class="dropdown-item" href="/bringConfig">Bring Config</a></li>
                      </ul>
                    </li>
                    <li class="container">
                      <a class="dropdown-item has-submenu" href="#" @click.prevent.stop="showConfigRegnskapSubmenu = !showConfigRegnskapSubmenu">
                        Regnskap
                        <font-awesome-icon :icon="['fas', showConfigRegnskapSubmenu ? 'chevron-up' : 'chevron-down']" class="submenu-arrow" />
                      </a>
                      <ul v-show="showConfigRegnskapSubmenu" class="list-unstyled ps-3">
                        <li class="container"><a class="dropdown-item" href="/unimicroConfig">Unimicro Config</a></li>
                      </ul>
                    </li>
                    <li class="container">
                      <a class="dropdown-item has-submenu" href="#" @click.prevent.stop="showConfigPspSubmenu = !showConfigPspSubmenu">
                        PSP
                        <font-awesome-icon :icon="['fas', showConfigPspSubmenu ? 'chevron-up' : 'chevron-down']" class="submenu-arrow" />
                      </a>
                      <ul v-show="showConfigPspSubmenu" class="list-unstyled ps-3">
                        <li class="container"><a class="dropdown-item" href="/vivaPartnerConfig">Viva Partner Config</a></li>
                        <li class="container"><a class="dropdown-item" href="/vivaConfig">Viva Miljø Config</a></li>
                        <li class="container"><a class="dropdown-item" href="/dinteroConfig">Dintero Miljø Config</a></li>
                      </ul>
                    </li>
                    <li class="container"><a class="dropdown-item" href="/siteModelConfig">AI Modell Config</a></li>
                    <li class="container"><a class="dropdown-item" href="/serviceAccounts">Tjenestekontoer</a></li>
                    <li class="container"><a class="dropdown-item" href="/subscriptionConfig">Abonnement Config</a></li>
                  </ul>
                </li>
                <li class="container">
                  <a class="dropdown-item has-submenu" href="#" @click.prevent.stop="showDiverseSubmenu = !showDiverseSubmenu">
                    Diverse
                    <font-awesome-icon :icon="['fas', showDiverseSubmenu ? 'chevron-up' : 'chevron-down']" class="submenu-arrow" />
                  </a>
                  <ul v-show="showDiverseSubmenu" class="list-unstyled ps-3">
                    <li class="container"><a class="dropdown-item" href="/aggregatedTurnoverReport">Samlet omsetningsrapport</a></li>
                    <li class="container"><a class="dropdown-item" href="/smartPickupTimeInsight">Smart hentetid innsikt</a></li>
                    <li class="container"><a class="dropdown-item" href="/cloudprnt">CloudPRNT</a></li>
                    <li class="container"><a class="dropdown-item" href="/cache">Cache</a></li>
                  </ul>
                </li>
              </ul>
            </li>
            <!-- Colour theme, immediately left of the profile menu. Styled to the navbar rather than
                 to the theme: that bar is bg-dark in both, so a themed background would put a near-black
                 control on a black bar. See bo_theme-select in backoffice.css. -->
            <li v-if="isSuperAdmin" class="nav-item d-flex align-items-center ms-md-5">
              <span class="bo_theme-picker">
                <!-- Sits INSIDE the select's own pill, left of the label, and tracks the choice rather
                     than the resolved theme — the select reads «Auto», so the icon shows the auto glyph
                     and not whichever palette the OS happens to be on. Decorative: the control keeps its
                     aria-label and its visible text, and pointer-events:none lets a click through to the
                     select underneath. A native <option> can only hold text, which is why the icon is
                     here and not on each of the three. -->
                <font-awesome-icon :icon="['fas', themeIcon]" class="bo_theme-picker__icon" />
                <select class="bo_theme-select" aria-label="Fargetema" :value="themeChoice"
                        @change="setTheme($event.target.value)">
                  <option value="system">Auto</option>
                  <option value="light">Lys</option>
                  <option value="dark">Mørkt</option>
                </select>
              </span>
            </li>
            <!-- The gap that separates this pair from the menu links belongs to whichever of the two
                 comes first. With the theme selector present (SUPER_ADMIN) it carries the ms-md-5 and the
                 profile sits tight beside it; without it, the profile takes the gap back or it crowds the
                 last menu item. -->
            <li class="nav-item dropdown" :class="isSuperAdmin ? 'ms-md-2' : 'ms-md-5'">
              <a class="nav-link dropdown-toggle" href="#" id="profile" role="button"
                 data-bs-toggle="dropdown" aria-expanded="false"><font-awesome-icon icon="user" /></a>
              <ul class="dropdown-menu" aria-labelledby="profile">
                <li class="container"><a class="dropdown-item" href="/profile">Profil</a></li>
                <li class="container"><a class="dropdown-item" @click.prevent="logOut">Logg ut</a></li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </nav>

    <div>
      <router-view />
      <!-- Outside the router view on purpose: every step points at the navigation, which is part of the
           shell rather than any page. Mounted here it survives navigation and does not restart. -->
      <TrainingTour ref="training" />
    </div>

    <ScrollToTop></ScrollToTop>

    <footer id="footer" class="mt-auto bg-dark">
      <div class="container clearfix">
        <span class="float-start" style="color:#909497">© {{currentYear}} {{merchant}}</span>
        <div class="float-end">
          <a class="footer-link" href="https://cagista.com">CAGISTA®</a>
        </div>
      </div>
    </footer>

    <feedback-modal ref="feedbackModal" @sent="onFeedbackSent" />
    <div v-if="feedbackThanks" class="feedback-thanks">Takk — vi har fått den.</div>
  </div>
</template>

<script>
import TrainingTour from '@/components/training/TrainingTour.vue';
import ScrollToTop from "@/components/other/ScrollToTop";
import FeedbackModal from "@/components/other/FeedbackModal.vue";
import UserUtil from "@/util/UserUtil";
import LocationUtil from "@/util/LocationUtil";
import MerchantUtil from "@/util/MerchantUtil";

export default {
  components: {ScrollToTop, TrainingTour, FeedbackModal},
  computed: {
    /*
     * The glyph beside the theme label. Font Awesome 5 (see plugins/font-awesome.js, which registers the
     * whole solid set) — so `adjust`, not v6's `circle-half-stroke`, which does not exist here and would
     * render as nothing at all rather than as an error.
     */
    themeIcon() {
      if (this.themeChoice === 'light') return 'sun';
      if (this.themeChoice === 'dark') return 'moon';
      return 'adjust';   // Auto: the half-filled circle, the conventional "follow the system" mark
    },

    useFunctionality(){
      return MerchantUtil.useFunctionality();
    },
    // SaaS features (Website Builder, VIVA-onboarding, Abonnement og betaling) — hidden for legacy
    // merchants (field missing/false); SUPER_ADMIN always sees them.
    useSaaSFeatures(){
      return MerchantUtil.useSaaSFeatures();
    },
    useOnlinePayment(){
      let locations = LocationUtil.getLocations();
      for(let i = 0 ; i < locations.length; i++){
        let location = locations[i];
        if(location.onlinePaymentProvider != 'NOT_ANY'){
          return true;
        }
      }
      return false;
    },
    hasMultiLocations(){
      return LocationUtil.hasMultiLocations();
    },
    /**
     * The accounting systems in use across this merchant's locations.
     *
     * Touches locationsRevision so the menu follows a locations refresh — localStorage is not
     * reactive, so without it a merchant who just picked their accounting system under Avdelinger
     * would not see the menu until the next full page load.
     */
    accountingSystems(){
      this.locationsRevision;
      return LocationUtil.getAccountingSystems();
    },
    usesAnyAccountingSystem(){
      return this.accountingSystems.size > 0;
    },
    usesUnimicro(){
      return this.accountingSystems.has('UNIMICRO');
    },
    usesFiken(){
      return this.accountingSystems.has('FIKEN');
    },
    usesTripletex(){
      return this.accountingSystems.has('TRIPLETEX');
    },
    isSuperAdmin(){
      return UserUtil.isSuperAdmin();
    },
    isEmployee(){
      return UserUtil.isEmployee();
    },
    isAccountant(){
      return UserUtil.isAccountant();
    },
    currentUser() {
      return this.$store.state.auth.user;
    },
    merchant() {
      if(this.currentUser){
        return this.$store.state.auth.user.merchant.toUpperCase();
      } else {
        return '';
      }
    },
    currentYear(){
      return (new Date()).getFullYear();
    },
    offerDeliveryService(){
      //True if any location offers delivery (offerDeliveryService is now per-location).
      return (LocationUtil.getLocations() || []).some(location => location.offerDeliveryService);
    },
    tableBookingEnabled(){
      //The Bordbookinger entry appears once any location takes bookings; switched on under
      //Innstillinger → Bordbooking, which is always there. Re-evaluated when the cached locations are
      //refreshed (locationsRevision), so the entry shows the moment the setting is saved.
      this.locationsRevision;
      return (LocationUtil.getLocations() || []).some(location => location.tableBooking && location.tableBooking.enabled);
    },
    userProfilePageName(){
      return MerchantUtil.userProfilePageName();
    },
    showUsersPage(){
      return MerchantUtil.showUsersPage();
    },
    payForSMS(){
      return MerchantUtil.payForSMS();
    },
    // A kitchen display at one of the merchant's locations — the same test the Kjøkkentid page filters its
    // locations by, so the entry never leads to a page that says there is none. Touches both revisions for the
    // same reason accountingSystems touches locationsRevision: the answers arrive after the menu has rendered.
    // A location that takes VIVA on the web, or whose tills and kiosks have a VIVA setup (the location list says
    // only that the block is there, never what is in it).
    usesViva(){
      this.locationsRevision;
      return (LocationUtil.getLocations() || []).some(location =>
        location.onlinePaymentProvider === 'VIVA' || (location.viva && location.viva.configured));
    },
    /**
     * Whether guests can eat in at any of the merchant's locations: the till or the kiosk offers «Spise her», or guests
     * order from the table by QR. Read the way TastyPOS and TastyKIOSK read it — no order types, or an empty list,
     * means both — so a location counts as dine-in unless it was set to «Ta med» alone. A list without posSetting
     * (a CagistaServices from before it was sent) also counts, so the report is never taken from anyone by mistake.
     */
    hasDineIn(){
      this.locationsRevision;
      const offersDineIn = (setting) => {
        const types = setting && setting.serviceOptions;
        return !Array.isArray(types) || types.length === 0 || types.includes('DINE_IN');
      };
      return (LocationUtil.getLocations() || []).some(location =>
        location.offerQR === true || offersDineIn(location.posSetting) || offersDineIn(location.kioskSetting));
    },
    usesKds(){
      this.terminalsRevision;
      this.locationsRevision;
      const locationIds = new Set((LocationUtil.getLocations() || []).map(location => location.id));
      return MerchantUtil.kdsLocationIds().some(locationId => locationIds.has(locationId));
    },
    offerDiscount(){
      return MerchantUtil.offerDiscount();
    },
    offerRewardProgram(){
      return MerchantUtil.offerRewardProgram();
    }
  },
  data() {
    return {
      feedbackThanks: false,
      themeChoice: 'light',   // 'light' | 'dark' | 'system'; see initTheme()
      systemDark: false,
      analyticsLoading: false,
      showRegnskapSubmenu: false,
      showTripletexSubmenu: false,
      showUnimicroSubmenu: false,
      showFikenSubmenu: false,
      showPspSubmenu: false,
      // Its own flag: «PSP» already names two other submenus in this nav — the merchant-level one
      // under Innstillinger and PSP Transactions under System — and they open independently.
      showConfigPspSubmenu: false,
      showConfigRegnskapSubmenu: false,
      showConfigPlatformSubmenu: false,
      showConfigDevicesSubmenu: false,
      showConfigStoreSubmenu: false,
      showPspTransactionsSubmenu: false,
      showConfigSubmenu: false,
      showDiverseSubmenu: false,
      // Bumped by MerchantUtil.refreshLocations() so menu entries that depend on the locations re-render.
      locationsRevision: 0,
      // Bumped by MerchantUtil.refreshKdsLocations(), for the Kjøkkentid entry.
      terminalsRevision: 0,
      // Whether the menu still fits on one row beside the logo. See measureNav().
      navFits: true
    };
  },
  watch: {
    $route(){
      //Keep the merchant setting and locations fresh on in-app navigation as well
      if(this.currentUser){
        MerchantUtil.refreshMerchantData();
      }
      // The theme follows who is signed in, and signing out is a router push: without this a SUPER_ADMIN's
      // dark stayed on the login page until it was reloaded.
      this.initTheme();
    }
  },
  mounted() {
    this.initTheme();
    window.addEventListener('cagista:locations-refreshed', () => { this.locationsRevision++; });
    window.addEventListener('cagista:terminals-refreshed', () => { this.terminalsRevision++; });
    // After the menu is rendered, because every step points at it — and only for a merchant who can act
    // on what it shows. An EMPLOYEE on a till has no use for a tour of settings and reports.
    this.$nextTick(() => {
      try {
        // SaaS merchants only. The Innstillinger step names Website Builder, Aktiver VIVA-betaling and
        // Abonnement og betaling — three entries a legacy merchant does not have — so the tour would be
        // describing a menu they cannot see. Same rule as everywhere else: existing merchants are not
        // reached by SaaS functionality. Not for SUPER_ADMIN, who starts it from Support → Opplæring (founder,
        // 2026-11-01).
        if (this.currentUser && this.useSaaSFeatures
            && !this.isEmployee && !this.isAccountant && !this.isSuperAdmin) {
          this.$refs.training?.startIfFirstRun();
        }
      } catch (e) {
        // Decoration must not be able to break the shell every page renders inside.
        console.log(e);
      }
    });
    //The merchant setting and locations are stored in localStorage at login. Refresh them
    //on every page load so changes apply without logging out and in again.
    if(this.currentUser){
      MerchantUtil.refreshMerchantData();
    }
    // If we're on the analytics page (after full page load), track loading state
    if (window.location.pathname === '/analytics') {
      this.analyticsLoading = true;
      this._analyticsInterval = setInterval(() => {
        if (window.__analyticsLoading === false) {
          this.analyticsLoading = false;
          clearInterval(this._analyticsInterval);
        }
      }, 300);
    }
    // Highlight the nav item matching the current page (nav uses plain <a href> full-page links,
    // so there's no router-link active class — derive it from the current path).
    this.$nextTick(() => this.highlightActiveNavItem());
    // The bar decides for itself when to collapse — see measureNav(). Measured after the menu is in
    // the DOM, and again whenever its width or its contents change.
    this.$nextTick(() => { this.measureNav(); this.publishNavOffset(); });
    this._onNavResize = () => this.scheduleNavMeasure();
    window.addEventListener('resize', this._onNavResize, { passive: true });
    this.$nextTick(() => {
      // The menu grows a step at a time as the merchant setting and the locations load, and each of
      // those changes the width it needs.
      if (this.$refs.navList && typeof MutationObserver !== 'undefined') {
        this._navObserver = new MutationObserver(() => this.scheduleNavMeasure());
        this._navObserver.observe(this.$refs.navList, { childList: true, subtree: true });
      }
    });
  },
  beforeUnmount() {
    if (this._analyticsInterval) {
      clearInterval(this._analyticsInterval);
    }
    if (this._themeMedia && this._onSystemTheme) {
      this._themeMedia.removeEventListener('change', this._onSystemTheme);
    }
    if (this._onNavResize) {
      window.removeEventListener('resize', this._onNavResize);
    }
    if (this._navObserver) {
      this._navObserver.disconnect();
    }
    if (this._navMeasureFrame) {
      cancelAnimationFrame(this._navMeasureFrame);
    }
  },
  methods: {
    openFeedback() {
      this.$refs.feedbackModal.open();
    },

    /* A receipt, not a modal on top of a modal. It clears itself; nothing depends on being dismissed. */
    onFeedbackSent() {
      this.feedbackThanks = true;
      setTimeout(() => { this.feedbackThanks = false; }, 4000);
    },

    /*
     * When the bar collapses to the hamburger.
     *
     * It used Bootstrap's navbar-expand-md, so it stayed on one row from 768px up no matter how many
     * items were in it — and the menu slid underneath the logo on the way down. No fixed breakpoint can
     * be right either: the row holds anywhere from four entries to eleven depending on the role, the
     * modules and whether table booking is on, so md collapses too late for a SUPER_ADMIN and xxl would
     * collapse far too early for an ACCOUNTANT.
     *
     * So it is measured. The row is laid out with nowrap, which makes a row that does not fit overflow
     * its container — visible as scrollWidth exceeding clientWidth — and that is the whole test.
     *
     * Collapsing removes the items from the row, so the same test cannot say when to expand again:
     * once stacked, everything fits. Hence _navBreakAt, the width it last failed at. Re-expansion is
     * attempted only above that width, and if it fails the mark moves up, so the two states cannot
     * oscillate around one pixel.
     */
    measureNav() {
      const shell = this.$refs.navShell;
      if (!shell) {
        return;
      }
      if (this.navFits) {
        if (shell.scrollWidth > shell.clientWidth + 1) {
          this._navBreakAt = window.innerWidth;
          this.navFits = false;
          this.$nextTick(() => this.publishNavOffset());
        }
      } else if (window.innerWidth > (this._navBreakAt || 0)) {
        this.navFits = true;
        // Re-measure once the row is actually back, in case it still does not fit.
        this.$nextTick(() => { this.measureNav(); this.publishNavOffset(); });
      }
    },

    /*
     * Publish the bar's height for anything on a page that sticks to the top of the window — a
     * category header, a table of contents. It is the sticky bar's height while the bar is one row,
     * and 0 once it collapses, since a stacked nav is not pinned. Elements read it as
     * `top: var(--bo-nav-offset, 0px)`, so a page that never sees it still behaves.
     */
    publishNavOffset() {
      const nav = this.$refs.nav;
      const offset = this.navFits && nav ? nav.offsetHeight : 0;
      document.documentElement.style.setProperty('--bo-nav-offset', offset + 'px');
    },

    scheduleNavMeasure() {
      if (this._navMeasureFrame) {
        return;
      }
      this._navMeasureFrame = requestAnimationFrame(() => {
        this._navMeasureFrame = null;
        this.measureNav();
      });
    },

    startTraining() {
      this.$refs.training?.start();
    },

    /*
     * Colour theme.
     *
     * The attribute goes on <html> as `data-bs-theme`, which is Bootstrap 5.3's own switch — every card,
     * table, form control, modal and dropdown in the app follows it without us writing a rule. Our own
     * styles follow through the tokens in backoffice.css.
     *
     * Three choices. 'system' does not store a palette, it refuses to: the OS is asked at load and again
     * whenever it changes, so a machine that dims itself at sunset takes an open tab with it.
     */
    initTheme() {
      if (!this._themeMedia) {
        const media = window.matchMedia('(prefers-color-scheme: dark)');
        this.systemDark = media.matches;
        this._themeMedia = media;
        this._onSystemTheme = (e) => { this.systemDark = e.matches; this.applyTheme(); };
        media.addEventListener('change', this._onSystemTheme);
      }
      /*
       * Founder, 2026-10-29: DARK for a SUPER_ADMIN, LIGHT for everyone else, and the selector only for a
       * SUPER_ADMIN (it was everyone's, with Auto, from 2026-09-22). Everyone else is pinned to light,
       * whatever this browser stored while the selector was theirs: with no selector a stored 'dark' or
       * 'system' would leave them in a theme they cannot get out of.
       *
       * Asked of UserUtil, not of the isSuperAdmin computed: that one reads localStorage, which Vue cannot
       * track, so it keeps its first answer and would miss a sign-out. index.html makes the same choice
       * before the first paint — keep the two in step.
       */
      if (!UserUtil.isSuperAdmin()) {
        this.themeChoice = 'light';
        this.applyTheme();
        return;
      }
      this.themeChoice = 'dark';
      const saved = localStorage.getItem('bo_theme');
      if (saved === 'light' || saved === 'dark' || saved === 'system') {
        this.themeChoice = saved;
      }
      this.applyTheme();
    },

    setTheme(choice) {
      this.themeChoice = choice;
      localStorage.setItem('bo_theme', choice);
      this.applyTheme();
    },

    applyTheme() {
      const resolved = this.themeChoice === 'system'
        ? (this.systemDark ? 'dark' : 'light')
        : this.themeChoice;
      document.documentElement.setAttribute('data-bs-theme', resolved);
    },

    isActive(path) {
      return window.location.pathname === path;
    },
    highlightActiveNavItem() {
      let path = window.location.pathname;
      if (!path) {
        return;
      }
      // The root path "/" renders the same page as Oversikt ("/overview") — highlight Oversikt there too.
      if (path === '/') {
        path = '/overview';
      }
      const links = document.querySelectorAll('#nav a.nav-link, #nav a.dropdown-item');
      links.forEach((link) => {
        const href = link.getAttribute('href');
        if (href && href !== '#' && href === path) {
          link.classList.add('active');
          // If the match is inside a dropdown, also highlight its top-level nav item.
          const topItem = link.closest('li.nav-item');
          const topLink = topItem ? topItem.querySelector('a.nav-link') : null;
          if (topLink) {
            topLink.classList.add('active');
          }
        }
      });
    },
    handleAnalyticsClick() {
      if (this.analyticsLoading) {
        return;
      }
      this.analyticsLoading = true;
      window.location.href = '/analytics';
    },
    logOut() {
      this.$store.dispatch('auth/logout');
      this.$router.push('/login');
    }
  }
};
</script>

<style lang="scss">
@use 'bootstrap/scss/bootstrap';

.dropdown-menu .dropdown-item {
  position: relative;
}

.dropdown-menu .submenu-arrow {
  font-size: 0.7em;
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
}

.dropdown-menu .ps-3 .submenu-arrow {
  right: calc(20px - 0.75rem);
}

footer {
  background-color:var(--bo-ink);
  color:var(--bo-surface);
  height:50px;
  padding-top:10px;
  padding-left:10px;
  padding-right:10px;

  position: relative;
  clear:both;
}

footer .nav>li>a {
  padding:3px;
  color:var(--bo-muted-2);
}

footer .nav>li>a:hover {
  background-color:transparent;
  color:#fff;
}

.footer-link{
  color:#909497;
  text-decoration: none;
}

.footer-link:hover{
  text-decoration: none;
}

.nav-link--disabled {
  opacity: 0.5;
  pointer-events: none;
  cursor: not-allowed;
}

/* Hamburger icon */
.hamburger-icon {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 28px;
  height: 24px;
  cursor: pointer;
}

/*
 * White, as a literal, and it must stay one.
 *
 * The navbar is bg-dark in BOTH themes, so everything drawn on it needs a fixed light colour. This said
 * var(--bo-surface), which reads as "white" only because that is its LIGHT value; in dark mode it is
 * #1c1f24 and the bars turned near-black on a black bar — an invisible hamburger, reported 2026-09-11.
 * The same mistake is what the theme-selector comment further up warns about. Anything on this bar that
 * looks like it wants a token wants a literal instead.
 */
.hamburger-icon span {
  display: block;
  width: 24px;
  height: 2px;
  background-color: #ffffff;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.hamburger-icon span:nth-child(1) {
  margin-bottom: 6px;
}

.hamburger-icon span:nth-child(3) {
  margin-top: 6px;
}

/* Animate hamburger to X when open */
.navbar-toggler[aria-expanded="true"] .hamburger-icon span:nth-child(1) {
  transform: translateY(8px) rotate(45deg);
}

.navbar-toggler[aria-expanded="true"] .hamburger-icon span:nth-child(2) {
  opacity: 0;
}

.navbar-toggler[aria-expanded="true"] .hamburger-icon span:nth-child(3) {
  transform: translateY(-8px) rotate(-45deg);
}

.navbar-toggler:focus {
  box-shadow: none;
}

/* Mobile nav menu styling */
/* The stacked menu panel.
 *
 * Keyed to the nav being stacked, not to a viewport width: the bar now collapses when its items
 * stop fitting beside the logo, which for a SUPER_ADMIN happens well above any phone breakpoint.
 * While these rules were a max-width media query, a menu that collapsed at 1300px opened as an
 * unstyled transparent list over the page. */
#nav.nav--stacked .mobile-nav {
  padding: 0.5rem 0;
  /* Opaque so the expanded menu fully covers the page content it overlays (matches bg-dark).
     Literal for the same reason as the hamburger: --bo-ink is #1a1a2e in light and #e8eaee in dark,
     so this panel turned near-WHITE in dark mode with its white links on top of it. This is that
     token's light value, so light mode is unchanged. */
  background-color: #1a1a2e;
}

#nav.nav--stacked .mobile-nav .navbar-nav {
  padding: 0.75rem 0;
  gap: 2px;
}

#nav.nav--stacked .mobile-nav .nav-link {
  padding: 0.65rem 1rem !important;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  font-size: 1.1rem;
}

#nav.nav--stacked .mobile-nav .nav-link:hover,
#nav.nav--stacked .mobile-nav .nav-link:focus {
  background-color: color-mix(in srgb, #ffffff 8%, transparent);
}

#nav.nav--stacked .mobile-nav .nav-item.dropdown .dropdown-menu {
  /* Redefine Bootstrap's dropdown link tokens for this dark panel rather than setting `color` on the
     items: custom properties inherit, so the active item (styled in navbar.css off the same token)
     resolves light here and dark on the white desktop panel, with one rule. */
  --bs-dropdown-link-color: rgba(255, 255, 255, 0.8);
  --bs-dropdown-link-hover-color: #ffffff;
  --bs-dropdown-link-active-color: #ffffff;
  background-color: #2c3034;
  border: none;
  border-radius: 8px;
  padding: 0.4rem 0;
  margin: 0.25rem 0 0.25rem 0.5rem;
}

#nav.nav--stacked .mobile-nav .nav-item.dropdown .dropdown-item {
  padding: 0.5rem 1rem;
  font-size: 1.05rem;
}

#nav.nav--stacked .mobile-nav .nav-item.dropdown .dropdown-item:hover,
#nav.nav--stacked .mobile-nav .nav-item.dropdown .dropdown-item:focus {
  background-color: color-mix(in srgb, #ffffff 10%, transparent);
}

#nav.nav--stacked .mobile-nav .nav-item.ms-md-5 {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

/* Those two utilities space the theme picker and the profile along the ROW, and they key off 768px —
   so once the bar could collapse above that width they started indenting the stacked panel instead.
   !important to beat Bootstrap's own, which is !important too. */
#nav.nav--stacked .mobile-nav .nav-item.ms-md-5,
#nav.nav--stacked .mobile-nav .nav-item.ms-md-2 {
  margin-left: 0 !important;
}

/* The theme picker is a <span>, not a .nav-link, so it does not get the 1rem the links above are
   given — and sat 16px left of every menu item and of the profile icon below it. Same inset, so its
   left edge lands on the text column. */
#nav.nav--stacked .mobile-nav .bo_theme-picker {
  margin-left: 1rem;
}

/* Smooth collapse animation */
#nav.nav--stacked .navbar-collapse.collapsing {
  transition: height 0.3s ease;
}

/*
  Centred, where the modal just was.

  It sat bottom-right and went unseen: the eye is in the middle of the screen at the moment the form
  closes, not in a corner. Above the navbar's z-index 1030, and it clears itself after four seconds —
  nothing depends on it being read or dismissed.
*/
.feedback-thanks {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1040;
  padding: 14px 22px;
  border-radius: 8px;
  background: var(--bo-ok-bg);
  color: var(--bo-ok-fg);
  border: 1px solid var(--bo-ok-line);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
  font-size: 1rem;
  font-weight: 600;
  text-align: center;
  /* It sits over the page for a moment; it must never swallow a click meant for what is behind. */
  pointer-events: none;
}
</style>
